import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Backdating UI for the Create Ticket form (ADR-021).
 *
 * Self-contained collapsible section that appears ONLY when the actor
 * holds `ticket.backdate` AND the tenant has the `ticket_backdating`
 * feature flag on. The parent (Create.tsx) renders this with the
 * relevant state hoisted in via props.
 *
 * Three modes the user can be in:
 *   - "now" (default): no backdate payload submitted. Form behaves
 *     exactly as today.
 *   - "earlier" + invalid: parent should not submit until valid.
 *   - "earlier" + valid: parent submits occurred_at + reason_code +
 *     reason_note alongside the rest of the ticket.
 *
 * Live policy feedback comes from `POST /tickets/validate-backdate`,
 * debounced so a fast typist doesn't fire one request per keystroke.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { utcIsoToZonedInput, zonedInputToUtcIso } from '@/lib/tz';
const REASONS = [
    { value: 'after_hours', label: 'After-hours emergency' },
    { value: 'walk_in', label: 'Walk-in / in-person request' },
    { value: 'engineer_forgot', label: 'Engineer forgot to log' },
    { value: 'verbal_auth', label: 'Verbal authorisation' },
    { value: 'system_outage', label: 'System was down' },
    { value: 'migration', label: 'Legacy / migration import' },
    { value: 'other', label: 'Other' },
];
const MIN_NOTE_CHARS = 10;
export const EMPTY_BACKDATING_STATE = {
    mode: 'now',
    occurredAtLocal: '',
    reasonCode: '',
    reasonNote: '',
};
export function BackdatingSection({ value, onChange, siteTimezone }) {
    const [serverHint, setServerHint] = useState(null);
    const [serverOk, setServerOk] = useState(null);
    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        if (value.mode !== 'earlier' || !value.occurredAtLocal) {
            setServerHint(null);
            setServerOk(null);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            try {
                // Interpret the typed wall-clock in the ON-SITE zone (not the
                // agent's browser) so an India agent backdating a foreign site
                // validates the correct instant. Browser-local fallback only when
                // the country isn't set yet.
                const occurredIso = siteTimezone
                    ? zonedInputToUtcIso(value.occurredAtLocal, siteTimezone)
                    : new Date(value.occurredAtLocal).toISOString();
                if (!occurredIso) {
                    setServerOk(null);
                    setServerHint(null);
                    return;
                }
                const resp = await api.post('/tickets/validate-backdate', {
                    occurred_at: occurredIso,
                });
                setServerOk(resp);
                setServerHint(null);
            }
            catch (err) {
                setServerOk(null);
                const e = err;
                setServerHint(e?.message ?? 'Could not validate against the tenant policy.');
            }
        }, 350);
        return () => {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [value.mode, value.occurredAtLocal, siteTimezone]);
    const setMode = (mode) => {
        if (mode === 'now') {
            onChange(EMPTY_BACKDATING_STATE);
        }
        else {
            onChange({ ...value, mode });
        }
    };
    const noteTooShort = value.reasonNote.trim().length > 0 && value.reasonNote.trim().length < MIN_NOTE_CHARS;
    // Upper bound for the picker = "now", expressed in the on-site zone so the
    // ceiling matches the zone the entered wall-clock is interpreted in. Falls
    // back to browser-local now when the country isn't set yet.
    const nowMaxLocal = siteTimezone
        ? utcIsoToZonedInput(new Date().toISOString(), siteTimezone)
        : new Date().toISOString().slice(0, 16);
    return (_jsxs("div", { className: "rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/40", children: [_jsx("div", { className: "mb-2 text-xs font-semibold text-slate-700 dark:text-slate-200", children: "\uD83D\uDCC5 When did this happen?" }), _jsxs("div", { className: "space-y-2 text-xs", children: [_jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "radio", name: "backdate-mode", checked: value.mode === 'now', onChange: () => setMode('now') }), _jsx("span", { children: "Now (creating in real-time)" })] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "radio", name: "backdate-mode", checked: value.mode === 'earlier', onChange: () => setMode('earlier') }), _jsx("span", { children: "This happened earlier \u2014 backdate it" })] })] }), value.mode === 'earlier' && (_jsxs("div", { className: "mt-3 space-y-3 border-t border-slate-200 pt-3 dark:border-slate-700", children: [_jsxs("label", { className: "block text-xs font-medium text-slate-700 dark:text-slate-200", children: ["Actual occurrence time ", _jsx("span", { className: "text-rose-500", children: "*" }), _jsx("input", { type: "datetime-local", value: value.occurredAtLocal, onChange: (e) => onChange({ ...value, occurredAtLocal: e.target.value }), max: nowMaxLocal, className: "mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs shadow-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" }), _jsx("div", { className: "mt-1 text-[11px] text-slate-500", children: siteTimezone
                                    ? _jsxs(_Fragment, { children: ["Enter the time in the ", _jsx("strong", { children: "on-site local zone" }), " (", siteTimezone, ") \u2014 set by the Country chosen for this ticket."] })
                                    : 'Enter the time in your local zone (choose the on-site Country to use the site’s zone instead).' }), serverOk && serverOk.gap_seconds !== null && (_jsxs("div", { className: "mt-1 text-[11px] text-slate-500", children: ["Your role:", ' ', _jsx("strong", { children: serverOk.actor_role_name ?? 'Unknown' }), serverOk.max_lookback_days != null && (_jsxs(_Fragment, { children: [" \u00B7 max lookback: ", serverOk.max_lookback_days, " days"] })), serverOk.max_lookback_days === null && _jsx(_Fragment, { children: " \u00B7 max lookback: unlimited" }), serverOk.long_lookback && (_jsx("span", { className: "ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", children: "long-lookback \u2014 will be flagged for compliance review" }))] })), serverHint && (_jsx("div", { className: "mt-1 text-[11px] text-rose-600 dark:text-rose-400", children: serverHint }))] }), _jsxs("fieldset", { className: "space-y-1.5", children: [_jsxs("legend", { className: "mb-1 block text-xs font-medium text-slate-700 dark:text-slate-200", children: ["Why is this backdated? ", _jsx("span", { className: "text-rose-500", children: "*" })] }), REASONS.map((r) => (_jsxs("label", { className: "flex items-center gap-2 text-xs", children: [_jsx("input", { type: "radio", name: "backdate-reason", value: r.value, checked: value.reasonCode === r.value, onChange: () => onChange({ ...value, reasonCode: r.value }) }), _jsx("span", { children: r.label })] }, r.value)))] }), _jsxs("label", { className: "block text-xs font-medium text-slate-700 dark:text-slate-200", children: ["Brief justification ", _jsx("span", { className: "text-rose-500", children: "*" }), _jsx("textarea", { rows: 3, maxLength: 4000, value: value.reasonNote, onChange: (e) => onChange({ ...value, reasonNote: e.target.value }), placeholder: "e.g. Customer paged at 11pm Saturday; engineer fixed firewall by 1am. Logging now for SLA + billing accuracy.", className: "mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs shadow-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900" }), _jsxs("div", { className: "mt-1 flex items-center justify-between text-[11px] text-slate-500", children: [_jsx("span", { children: "Stored permanently in the audit trail." }), _jsxs("span", { className: noteTooShort ? 'text-rose-600' : '', children: [value.reasonNote.trim().length, "/", MIN_NOTE_CHARS, " chars min"] })] })] }), _jsx("div", { className: "rounded-md border border-slate-200 bg-white p-2 text-[11px] text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300", children: "\u24D8 This will be set as the ticket\u2019s official occurrence time. SLA targets compute from it. Customer ack email + CSAT may be suppressed per your tenant\u2019s policy. Audit trail records both the system creation time AND this backdate." })] }))] }));
}
/**
 * Quick client-side validator the parent uses to gate Submit. Returns
 * null when valid, a human-readable error string when not. The server
 * is the source of truth for the lookback policy; this just catches
 * obvious local mistakes before the network round-trip.
 */
export function validateBackdatingClientSide(s, siteTimezone) {
    if (s.mode === 'now')
        return null;
    if (!s.occurredAtLocal)
        return 'Pick the actual occurrence time.';
    // Interpret the wall-clock in the on-site zone (when known) so the
    // future-time guard matches the instant the server will compute.
    const occurredIso = siteTimezone ? zonedInputToUtcIso(s.occurredAtLocal, siteTimezone) : null;
    const occurredMs = occurredIso ? new Date(occurredIso).getTime() : new Date(s.occurredAtLocal).getTime();
    if (Number.isNaN(occurredMs))
        return 'Occurrence time is invalid.';
    if (occurredMs >= Date.now())
        return 'Occurrence time cannot be in the future.';
    if (!s.reasonCode)
        return 'Pick a reason for backdating.';
    if (s.reasonNote.trim().length < MIN_NOTE_CHARS) {
        return `Justification must be at least ${MIN_NOTE_CHARS} characters.`;
    }
    return null;
}
